/**
 * Location Management - 库位管理列表页
 * 简单列表 + FilterPanel + 详情 Drawer
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Form, Input, Select, Button, Table, Space, Col, message, Image, Upload, Modal, Spin } from 'antd';
import EllipsisCell from '../../../../components/common/EllipsisCell';
import type { UploadFile, UploadProps } from 'antd';
import { ExportOutlined, ImportOutlined, InboxOutlined, LoadingOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useTableScrollHeight } from '@/hooks/useTableScrollHeight';
import dayjs from 'dayjs';
import FilterPanel from '../../../../components/common/FilterPanel';
import type { LocationDTO, QueryLocationPageRequest } from '../../../../types/store-resource/location';
import type { Paging } from '../../../../types/common';
import * as locationService from '../../../../services/store-resource/locationService';
import * as itemService from '../../../../services/master-data/itemService';

const LocationList: React.FC = () => {
  const { t } = useTranslation();
  const { tableWrapRef, scrollY } = useTableScrollHeight();
  const [searchForm] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [dataSource, setDataSource] = useState<LocationDTO[]>([]);
  const [pagination, setPagination] = useState<Paging>({ currentPage: 1, pageSize: 10, totalCount: 0 });

  // Item remote search
  const [itemOptions, setItemOptions] = useState<{ label: string; value: string }[]>([]);
  const [itemSearching, setItemSearching] = useState(false);
  const itemSearchTimer = useRef<ReturnType<typeof setTimeout>>();

  const handleItemSearch = (keyword: string) => {
    if (itemSearchTimer.current) clearTimeout(itemSearchTimer.current);
    if (!keyword || keyword.length < 2) {
      setItemOptions([]);
      return;
    }
    itemSearchTimer.current = setTimeout(async () => {
      setItemSearching(true);
      try {
        const res = await itemService.pageItem({ currentPage: 1, pageSize: 20, code: keyword });
        if (res.success && res.data) {
          setItemOptions(
            res.data.tableData.map((item) => ({
              label: `${item.code} - ${item.name || ''}`,
              value: item.code,
            }))
          );
        }
      } finally {
        setItemSearching(false);
      }
    }, 300);
  };

  // Import modal state
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [fileList, setFileList] = useState<UploadFile[]>([]);
  const [importLoading, setImportLoading] = useState(false);

  const uploadProps: UploadProps = {
    name: 'file',
    multiple: false,
    accept: '.xlsx,.xls',
    fileList,
    beforeUpload: (file) => {
      setFileList([file as unknown as UploadFile]);
      return false;
    },
    onRemove: () => {
      setFileList([]);
    },
  };

  const handleImportCancel = () => {
    setImportModalVisible(false);
    setFileList([]);
  };

  const handleUpload = async () => {
    if (fileList.length === 0) {
      message.error(t('location.list.import.selectFile'));
      return;
    }

    const file = fileList[0] as unknown as File;
    setImportLoading(true);

    try {
      message.loading(t('location.list.import.uploading'), 0);
      const response = await locationService.importLocationFile(file);

      if ((response as any).data?.success !== false) {
        message.destroy();
        message.success(t('location.list.import.success'));
        setImportModalVisible(false);
        setFileList([]);
        loadData(buildQueryParams(1, pagination.pageSize));
      } else {
        message.destroy();
        message.error((response as any).data?.errorMessage || t('location.list.import.fail'));
      }
    } catch (err: any) {
      message.destroy();
      console.error('Import error:', err);
      message.error(err.message || t('location.list.import.failRetry'));
    } finally {
      setImportLoading(false);
    }
  };

  // 加载列表数据
  const loadData = useCallback(async (params: QueryLocationPageRequest) => {
    setLoading(true);
    try {
      const response = await locationService.pageLocation(params);
      if (response.success && response.data) {
        setDataSource(response.data.tableData);
        setPagination(response.data.paging);
      } else {
        message.error(response.errorMessage || t('common.message.loadFailed'));
      }
    } catch {
      message.error(t('common.message.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  // 初始化
  useEffect(() => {
    loadData({ currentPage: 1, pageSize: 10 });
  }, []);

  // 构建查询参数
  const buildQueryParams = (page: number, pageSize: number): QueryLocationPageRequest => {
    const values = searchForm.getFieldsValue();
    return {
      currentPage: page,
      pageSize,
      warehouseCode: values.warehouseCode || undefined,
      ownerCode: values.ownerCode || undefined,
      itemCode: values.itemCode || undefined,
      aisleCode: values.aisleCode || undefined,
      locationCode: values.locationCode || undefined,
      locationType: values.locationType || undefined,
    };
  };

  // 搜索
  const handleSearch = () => {
    loadData(buildQueryParams(1, pagination.pageSize));
  };

  // 重置
  const handleReset = () => {
    searchForm.resetFields();
    loadData({ currentPage: 1, pageSize: pagination.pageSize });
  };

  // 分页变更
  const handlePageChange = (page: number, pageSize: number) => {
    loadData(buildQueryParams(page, pageSize));
  };

  // 导出
  const [exportLoading, setExportLoading] = useState(false);
  const handleExport = async () => {
    setExportLoading(true);
    try {
      const values = searchForm.getFieldsValue();
      const response = await locationService.exportLocation({
        itemCode: values.itemCode || undefined,
        locationCode: values.locationCode || undefined,
      });
      if (response.success && response.data) {
        const link = document.createElement('a');
        link.href = response.data;
        link.download = '';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        message.success(t('location.list.export.success'));
      } else {
        message.error(response.errorMessage || t('location.list.export.fail'));
      }
    } catch {
      message.error(t('location.list.export.fail'));
    } finally {
      setExportLoading(false);
    }
  };


  // 格式化日期
  const formatDateTime = (timestamp: number | null) => {
    return timestamp ? dayjs(timestamp).format('DD-MM-YYYY HH:mm:ss') : '-';
  };

  // 库位类型选项
  const locationTypeOptions = [
    { value: 'STORAGE', label: t('location.list.locationType.STORAGE') },
    { value: 'PICKING', label: t('location.list.locationType.PICKING') },
    { value: 'STAGING', label: t('location.list.locationType.STAGING') },
  ];

  // 表格列定义
  const columns = [
    {
      title: t('location.list.table.warehouseCode'),
      dataIndex: 'warehouseCode',
      key: 'warehouseCode',
      width: 160,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('location.list.table.ownerCode'),
      dataIndex: 'ownerCode',
      key: 'ownerCode',
      width: 130,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('location.list.table.itemCode'),
      dataIndex: 'itemCode',
      key: 'itemCode',
      width: 130,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('location.list.table.itemName'),
      dataIndex: 'itemName',
      key: 'itemName',
      width: 180,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('location.list.table.image'),
      dataIndex: 'imageUrl',
      key: 'imageUrl',
      width: 90,
      render: (url: string | null) => url ? (
        <Image src={url} width={40} height={40} style={{ objectFit: 'cover', borderRadius: 4 }} fallback="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAAAEAAAABCAYAAAAfFcSJAAAADUlEQVR42mN88P/BfwAJhAPk3KFb2AAAAABJRU5ErkJggg==" />
      ) : '-',
    },
    {
      title: t('location.list.table.aisleCode'),
      dataIndex: 'aisleCode',
      key: 'aisleCode',
      width: 130,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('location.list.table.locationCode'),
      dataIndex: 'locationCode',
      key: 'locationCode',
      width: 180,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('location.list.table.locationType'),
      dataIndex: 'locationType',
      key: 'locationType',
      width: 160,
      render: (text: string) => <EllipsisCell>{text}</EllipsisCell>,
    },
    {
      title: t('location.list.table.updatedTime'),
      dataIndex: 'gmtModified',
      key: 'gmtModified',
      width: 180,
      sorter: true,
      render: (time: number | null) => <EllipsisCell>{formatDateTime(time)}</EllipsisCell>,
    },
  ];

  return (
    <div style={{ padding: '16px', height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* 筛选区域 */}
      <FilterPanel
        form={searchForm}
        onFilter={handleSearch}
        onReset={handleReset}
        loading={loading}
      >
        <Col span={6}>
          <Form.Item name="itemCode" label={t('location.list.search.item')}>
            <Select
              showSearch
              allowClear
              placeholder={t('location.list.search.placeholder.item')}
              filterOption={false}
              onSearch={handleItemSearch}
              notFoundContent={itemSearching ? <Spin size="small" /> : null}
              options={itemOptions}
            />
          </Form.Item>
        </Col>
        <Col span={6}>
          <Form.Item name="locationCode" label={t('location.list.search.locationCode')}>
            <Input placeholder={t('location.list.search.placeholder.locationCode')} allowClear />
          </Form.Item>
        </Col>
      </FilterPanel>

      {/* 操作栏 */}
      <div style={{ marginTop: 16, marginBottom: 16, display: 'flex', justifyContent: 'flex-end', flexShrink: 0 }}>
        <Button
          icon={exportLoading ? <LoadingOutlined /> : <ExportOutlined />}
          onClick={handleExport}
          loading={exportLoading}
        >
          {t('location.list.actions.export')}
        </Button>
      </div>

      {/* 表格 */}
      <div ref={tableWrapRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden', marginTop: 16 }}>
        <Table
          rowKey="locationCode"
          columns={columns}
          dataSource={dataSource}
          loading={loading}
          scroll={{ x: 1270, y: scrollY }}
          pagination={{
            current: pagination.currentPage,
            pageSize: pagination.pageSize,
            total: pagination.totalCount,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
            showQuickJumper: true,
            showTotal: (total) => t('location.list.pagination.total', { total }),
            onChange: handlePageChange,
          }}
        />
      </div>

      {/* Import Modal */}
      <Modal
        title={t('location.list.import.title')}
        open={importModalVisible}
        onOk={handleUpload}
        onCancel={handleImportCancel}
        confirmLoading={importLoading}
        width={600}
      >
        <div style={{ marginBottom: '16px' }}>
          <p>{t('location.list.import.description')}</p>
          <p style={{ color: '#666', fontSize: '12px' }}>
            {t('location.list.import.supportedFormats')}
          </p>
        </div>

        <Upload.Dragger {...uploadProps}>
          <p className="ant-upload-drag-icon">
            <InboxOutlined />
          </p>
          <p className="ant-upload-text">{t('location.list.import.dragText')}</p>
          <p className="ant-upload-hint">{t('location.list.import.hint')}</p>
        </Upload.Dragger>
      </Modal>
    </div>
  );
};

export default LocationList;
